import { API_URL } from '../../../../constants/urls.constants';
import { getErrorMessage } from '../../../../lib/error';
import { toast } from '../../../../lib/toast';
import type { GetQueryArgsType } from '../../../../types/global.types';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import { setPermissionsInfo, userLoggedOut } from '../../auth/auth.slice';
import type {
  GetAllRolesParams,
  GetRoleByOrgParams,
  GetRoleUsersParams,
  ModulesResponse,
  RoleByOrgResponse,
  RoleUsersResponse,
  RolesResponse,
  UserPermissionResponse,
} from './role-permission.type';

const { ROLES } = APP_API_TAGS || {};

export const rolePermissionApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ------ Start: API Endpoints for Role Management ------

    // Create a new role
    createRole: builder.mutation({
      query: (data) => ({
        url: API_URL.roles,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ROLES],
    }),

    createRoleWithUsers: builder.mutation({
      query: (data) => ({
        url: `${API_URL.roles}/upsert-with-users`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ROLES],
    }),

    updateRoleWithUsers: builder.mutation({
      query: (data) => ({
        url: `${API_URL.roles}/update-with-users`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [ROLES],
    }),

    // Update an existing role
    updateRole: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.roles}/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (res, __, arg) => {
        return res ? [{ type: ROLES, id: arg.id }, ROLES] : [];
      },
    }),

    // Get all roles with filtering and pagination
    getAllRoles: builder.query<
      RolesResponse,
      GetQueryArgsType<GetAllRolesParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.roles,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [ROLES],
    }),

    // Get a specific role by ID and organization ID
    getRoleByOrgId: builder.query<RoleByOrgResponse, GetRoleByOrgParams>({
      query: ({ id, organization_id }) => ({
        url: `${API_URL.roles}/id/${id}/org/${organization_id}`,
        method: 'GET',
      }),
      providesTags: (res, __, arg) => {
        return res ? [{ type: ROLES, id: arg.id }, ROLES] : [];
      },
    }),

    // Get users assigned to a specific role
    getRoleUsers: builder.query<RoleUsersResponse, GetRoleUsersParams>({
      query: ({ roleId, organizationId, page = 1, limit = 1000 }) => ({
        url: `${API_URL.getRoleUsers}/${roleId}/organization/${organizationId}/users`,
        method: 'GET',
        params: { page, limit },
      }),
      providesTags: [ROLES],
    }),

    // Assign roles to multiple employees in bulk
    assignBulkRole: builder.mutation({
      query: (data) => ({
        url: API_URL.bulkAssignRoles,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ROLES],
    }),

    // Delete a role
    deleteRole: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.roles}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res, __, arg) => {
        return res ? [{ type: ROLES, id: arg.id }, ROLES] : [];
      },
    }),

    // Activate a role
    activateRole: builder.mutation({
      query: (roleId: string) => ({
        url: `${API_URL.roles}/${roleId}/activate`,
        method: 'PATCH',
      }),
      invalidatesTags: [ROLES],
    }),

    // Deactivate a role
    deactivateRole: builder.mutation({
      query: (roleId: string) => ({
        url: `${API_URL.roles}/${roleId}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: [ROLES],
    }),
    // Get all modules for an organization
    getAllModules: builder.query<ModulesResponse, { organizationId: string }>({
      query: ({ organizationId }) => ({
        url: `${API_URL.roles}/organization/${organizationId}/modules`,
        method: 'GET',
      }),
      providesTags: [ROLES],
    }),

    // Get permissions for a specific user
    getUserPermission: builder.query<
      UserPermissionResponse,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `${API_URL.roles}/user/${userId}/permissions`,
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.success && data?.data) {
            dispatch(setPermissionsInfo(data.data));
          }
        } catch (error) {
          console.log(error, 'From permission get roles');
          localStorage.clear();
          dispatch(userLoggedOut());
          toast('error', getErrorMessage(error, 'You have no access.'));
        }
      },
      providesTags: [ROLES],
    }),
  }),
});

// Export hooks
export const {
  useCreateRoleMutation,
  useCreateRoleWithUsersMutation,
  useUpdateRoleWithUsersMutation,
  useUpdateRoleMutation,
  useGetAllRolesQuery,
  useGetRoleByOrgIdQuery,
  useGetRoleUsersQuery,
  useAssignBulkRoleMutation,
  useDeleteRoleMutation,
  useGetAllModulesQuery,
  useGetUserPermissionQuery,
  useActivateRoleMutation,
  useDeactivateRoleMutation,
} = rolePermissionApi;
